(() => {
  'use strict';
  const root = document.querySelector('[data-bbc-diaper-dash]');
  if (!root) return;
  const personalisation = window.BBC_DIAPER_DASH_PERSONALISATION;
  const audioApi = window.BBC_DIAPER_DASH_AUDIO;
  const cloudApi = window.BBC_DIAPER_DASH_CLOUD;
  const cloudSyncEnabled = root.dataset.cloudSyncEnabled === 'true';
  if (!personalisation || !audioApi || !cloudApi || (cloudSyncEnabled && typeof cloudApi.createCloudSync !== 'function')) {
    root.querySelector('[data-game-error]').hidden = false;
    return;
  }
  root.style.setProperty('--dash-nursery', `url("${root.dataset.assetNursery}")`);
  for (let number = 1; number <= 5; number++) {
    root.style.setProperty(`--dash-partner-${number}`, `url("${root.getAttribute(`data-asset-partner-${number}`)}")`);
  }

  const screens = [...root.querySelectorAll('[data-screen]')];
  const menu = root.querySelector('[data-menu]');
  const scrim = root.querySelector('[data-menu-scrim]');
  const pausePanel = root.querySelector('[data-pause-panel]');
  const menuButton = root.querySelector('[data-action="open-menu"]');
  const partnerStage = root.querySelector('[data-partner-reaction]');
  const reactionBubble = root.querySelector('[data-reaction-text]');
  const gameOverReaction = root.querySelector('[data-game-over-reaction]');
  const nameInput = root.querySelector('#bbc-diaper-dash-partner-name');
  const partnerPortrait = root.querySelector('.bbc-diaper-dash__partner-portrait');
  const soundInput = root.querySelector('[data-setting="sound"]');
  const voiceInput = root.querySelector('[data-setting="voice"]');
  const choices = [...root.querySelectorAll('[data-partner-choice]')];
  const defaultLabels = Object.fromEntries(choices.map(choice => [choice.dataset.partnerChoice, choice.querySelectorAll('span')[1].textContent.trim()]));
  const dialogue = {};
  root.querySelectorAll('[data-reaction-mood]').forEach(line => {
    const mood = line.dataset.reactionMood;
    (dialogue[mood] ||= []).push(line.textContent.trim());
  });
  const effectSources = Object.fromEntries(Object.keys(audioApi.PATTERNS).map(kind => [kind, root.getAttribute(`data-audio-${kind}`) || '']));
  const voiceSources = Object.fromEntries(['happy', 'concerned', 'annoyed', 'angry', 'furious'].map(mood => [mood, (root.getAttribute(`data-voice-${mood}`) || '').split('|').filter(Boolean)]));
  const audio = audioApi.createAudioManager({
    audioContextFactory: () => {
      const Context = window.AudioContext || window.webkitAudioContext;
      return Context ? new Context() : null;
    },
    audioFactory: source => new window.Audio(source),
    effectSources,
    voiceSources
  });
  const storage = (() => { try { return window.localStorage; } catch { return null; } })();
  const syncOptions = {
    account: () => window.BBC_ACCOUNT, accountTarget: window, storage, onlineTarget: window, isOnline: () => navigator.onLine !== false,
    onUpdate: snapshot => {
      if (cloudSyncEnabled) {
        root.dataset.authState = snapshot.userId ? 'signed-in' : 'guest';
        root.querySelectorAll('[data-cloud-only]').forEach(element => {
          element.hidden = element.dataset.cloudOnly === 'signed-in' ? !snapshot.userId : Boolean(snapshot.userId);
        });
        const presence = root.querySelector('[data-account-presence]');
        if (presence) presence.textContent = snapshot.userId ? root.dataset.labelSignedIn : root.dataset.labelGuest;
        const saveStatus = root.querySelector('[data-save-status]');
        if (saveStatus) {
          saveStatus.hidden = !snapshot.userId;
          saveStatus.textContent = root.dataset[`labelSave${snapshot.status[0].toUpperCase()}${snapshot.status.slice(1)}`] || '';
        }
      }
      if (!samePreferences(settings, snapshot.preferences)) { settings = snapshot.preferences; applySettings(); }
      const bestChanged = storedBest !== snapshot.best;
      storedBest = snapshot.best;
      if (bestChanged) engine?.setBest(storedBest);
      if (!engine) root.querySelector('[data-best-score]').textContent = String(storedBest);
    }
  };
  const cloud = cloudSyncEnabled
    ? cloudApi.createCloudSync(syncOptions)
    : cloudApi.createLocalState({ storage, onUpdate: syncOptions.onUpdate });
  const samePreferences = (a, b) => JSON.stringify(a) === JSON.stringify(b);
  const initial = cloud.getSnapshot();
  let settings = initial.preferences;
  let storedBest = initial.best;
  let activeScreen = 'opening';
  let engine = null;
  let returnFocus = null;
  let menuWasRunning = false;
  let runOwnerId = null;
  let lastState = null;
  let lastReactionLine = '';
  let reactionTimer = null;
  let gameOverTimeout = null;

  const saveBest = value => {
    if (value <= storedBest) return;
    storedBest = value;
    if (runOwnerId === cloud.getSnapshot().userId) cloud.recordBest(value);
  };
  const saveSettings = () => {
    settings = personalisation.normalizePreferences(settings);
    cloud.changePreferences(settings);
  };
  const partnerLabel = id => settings.names[id] || defaultLabels[id] || defaultLabels['1'];
  const updatePartnerLabel = () => {
    root.dataset.partnerChoice = settings.selectedPartnerId;
    const partnerSheet = root.getAttribute(`data-asset-partner-${settings.selectedPartnerId}`);
    if (partnerSheet && partnerPortrait) partnerPortrait.style.backgroundImage = `url("${partnerSheet}")`;
    root.querySelector('[data-partner-name]').textContent = partnerLabel(settings.selectedPartnerId);
    choices.forEach(choice => {
      const label = partnerLabel(choice.dataset.partnerChoice);
      choice.querySelectorAll('span')[1].textContent = label;
      choice.setAttribute('aria-label', label);
    });
  };
  const applySettings = () => {
    choices.forEach(choice => {
      const selected = choice.dataset.partnerChoice === settings.selectedPartnerId;
      choice.classList.toggle('is-selected', selected);
      choice.setAttribute('aria-pressed', String(selected));
    });
    nameInput.value = settings.names[settings.selectedPartnerId] || '';
    soundInput.checked = settings.soundEffectsEnabled;
    voiceInput.checked = settings.voiceReactionsEnabled;
    updatePartnerLabel();
  };
  const finishNameEdit = () => { nameInput.value = settings.names[settings.selectedPartnerId] || ''; };
  const hideReaction = () => {
    if (reactionTimer) window.clearTimeout(reactionTimer);
    reactionTimer = null;
    reactionBubble.hidden = true;
    reactionBubble.textContent = '';
    partnerStage.classList.remove('is-reacting');
  };
  const showReaction = (mood, persistent = false) => {
    hideReaction();
    const line = personalisation.chooseReaction(dialogue[mood], lastReactionLine);
    if (!line) return;
    lastReactionLine = line;
    reactionBubble.textContent = line;
    reactionBubble.hidden = false;
    partnerStage.classList.add('is-reacting');
    if (persistent) {
      gameOverReaction.textContent = line;
      gameOverReaction.hidden = false;
    } else reactionTimer = window.setTimeout(hideReaction, 2600);
  };
  const showScreen = name => {
    activeScreen = name;
    screens.forEach(screen => { screen.hidden = screen.dataset.screen !== name; });
    if (name !== 'play') pausePanel.hidden = true;
  };
  const renderState = state => {
    const before = lastState;
    root.querySelector('[data-current-score]').textContent = String(state.score);
    root.querySelector('[data-best-score]').textContent = String(state.best);
    root.querySelector('[data-anger-score]').textContent = String(state.anger);
    root.querySelector('[data-anger-meter]').setAttribute('aria-valuenow', String(state.anger));
    const angerFill = root.querySelector('[data-anger-fill]');
    const angerHue = Math.max(0, 48 - state.anger * 1.6);
    angerFill.style.height = `${state.anger / 30 * 100}%`;
    angerFill.style.background = `linear-gradient(0deg, hsl(${angerHue + 7} 100% 58%), hsl(${angerHue} 96% 55%))`;
    partnerStage.dataset.mood = state.mood;
    root.dataset.gamePaused = state.status !== 'playing' ? 'true' : 'false';
    if (before?.status === 'playing') {
      const scoreGain = state.score - before.score;
      if (scoreGain > 0) {
        const milestone = Math.floor(state.score / 20) > Math.floor(before.score / 20);
        audio.playEffect(milestone ? 'milestone' : scoreGain === 5 ? 'catch-pee' : 'catch-poop', settings.soundEffectsEnabled);
      }
      if (state.anger > before.anger) {
        showReaction(state.mood, state.status === 'game-over');
        audio.playEffect(state.status === 'game-over' ? 'game-over' : 'miss', settings.soundEffectsEnabled);
        audio.playVoice(state.mood, settings.voiceReactionsEnabled);
        const meter = root.querySelector('[data-anger-meter]');
        meter.classList.remove('is-flashing');
        void meter.offsetWidth;
        meter.classList.add('is-flashing');
      }
    }
    saveBest(state.best);
    lastState = state;
  };
  const showGameOver = state => {
    root.querySelector('[data-final-score]').textContent = String(state.score);
    root.querySelector('[data-final-best]').textContent = String(state.best);
    if (gameOverTimeout) window.clearTimeout(gameOverTimeout);
    gameOverTimeout = window.setTimeout(() => {
      showScreen('game-over');
      root.querySelector('[data-action="play-again"]')?.focus({ preventScroll: true });
    }, 700);
  };
  const ensureEngine = () => {
    if (engine) return engine;
    if (!window.BBC_DIAPER_DASH_ENGINE) throw new Error('Game engine unavailable');
    const defaults = window.BBC_DIAPER_DASH_RULES.DEFAULT_CONFIG;
    const mount = root.querySelector('[data-phaser-mount]');
    const logicalHeight = Math.max(defaults.height, Math.round(defaults.width * mount.clientHeight / Math.max(1, mount.clientWidth)));
    const catcherY = logicalHeight - (defaults.height - defaults.catcherY);
    const spawnY = defaults.babyY + defaults.emitterOffsetY;
    const travelRatio = (catcherY - spawnY) / (defaults.catcherY - spawnY);
    engine = window.BBC_DIAPER_DASH_ENGINE.createEngine({
      mount, best: storedBest,
      config: { height: logicalHeight, catcherY, peeSpeed: defaults.peeSpeed * travelRatio, poopSpeed: defaults.poopSpeed * travelRatio },
      assets: { baby: root.dataset.assetBaby, babyPlayful: root.dataset.assetBabyPlayful, diaper: root.dataset.assetDiaper, pee: root.dataset.assetPee, poop: root.dataset.assetPoop },
      onUpdate: renderState, onGameOver: showGameOver
    });
    return engine;
  };
  const begin = () => {
    root.querySelector('[data-game-error]').hidden = true;
    if (gameOverTimeout) window.clearTimeout(gameOverTimeout);
    gameOverTimeout = null;
    hideReaction();
    gameOverReaction.hidden = true;
    gameOverReaction.textContent = '';
    lastState = null;
    runOwnerId = cloud.getSnapshot().userId;
    try {
      showScreen('play');
      audio.unlock();
      ensureEngine().start();
      renderState(engine.getState());
    } catch {
      showScreen('opening');
      root.querySelector('[data-game-error]').hidden = false;
    }
  };
  const pause = focus => {
    if (activeScreen !== 'play' || engine?.getState().status !== 'playing') return;
    engine.pause();
    hideReaction();
    pausePanel.hidden = false;
    if (focus) pausePanel.querySelector('button')?.focus({ preventScroll: true });
    audio.suspend();
  };
  const resume = () => {
    if (activeScreen !== 'play') return;
    pausePanel.hidden = true;
    engine?.resume();
    audio.resume();
    root.querySelector('[data-action="pause"]')?.focus({ preventScroll: true });
  };
  const openMenu = () => {
    if (activeScreen !== 'play' || !menu.hidden) return;
    returnFocus = document.activeElement;
    menuWasRunning = engine?.getState().status === 'playing';
    if (menuWasRunning) engine.pause();
    audio.stopVoice();
    hideReaction();
    pausePanel.hidden = true;
    menu.hidden = false;
    scrim.hidden = false;
    menuButton?.setAttribute('aria-expanded', 'true');
    audio.playEffect('menu', settings.soundEffectsEnabled);
    menu.querySelector('[data-partner-choice]')?.focus({ preventScroll: true });
  };
  const closeMenu = () => {
    if (menu.hidden) return;
    finishNameEdit();
    menu.hidden = true;
    scrim.hidden = true;
    menuButton?.setAttribute('aria-expanded', 'false');
    audio.playEffect('menu', settings.soundEffectsEnabled);
    if (menuWasRunning && !document.hidden) resume();
    else if (engine?.getState().status === 'paused') pausePanel.hidden = false;
    menuWasRunning = false;
    (returnFocus || menuButton)?.focus({ preventScroll: true });
  };

  root.querySelector('[data-action="start"]')?.addEventListener('click', begin);
  root.querySelector('[data-action="play-again"]')?.addEventListener('click', begin);
  root.querySelector('[data-action="pause"]')?.addEventListener('click', () => pause(true));
  root.querySelector('[data-action="open-menu"]')?.addEventListener('click', openMenu);
  root.querySelector('[data-action="close-menu"]')?.addEventListener('click', closeMenu);
  scrim?.addEventListener('click', closeMenu);
  root.querySelectorAll('[data-action="resume"]').forEach(button => button.addEventListener('click', () => {
    if (!menu.hidden) { menuWasRunning = true; closeMenu(); }
    else resume();
  }));
  choices.forEach(button => button.addEventListener('click', () => {
    finishNameEdit();
    settings.selectedPartnerId = button.dataset.partnerChoice;
    applySettings();
    saveSettings();
    audio.playEffect('menu', settings.soundEffectsEnabled);
  }));
  nameInput.addEventListener('input', () => {
    const limited = personalisation.limitNameInput(nameInput.value);
    if (nameInput.value !== limited) nameInput.value = limited;
    const name = personalisation.normalizeName(limited);
    if (name) settings.names[settings.selectedPartnerId] = name;
    else delete settings.names[settings.selectedPartnerId];
    updatePartnerLabel();
    saveSettings();
  });
  nameInput.addEventListener('blur', finishNameEdit);
  soundInput.addEventListener('change', () => {
    settings.soundEffectsEnabled = soundInput.checked;
    saveSettings();
    audio.playEffect('menu', settings.soundEffectsEnabled);
  });
  voiceInput.addEventListener('change', () => {
    settings.voiceReactionsEnabled = voiceInput.checked;
    if (!settings.voiceReactionsEnabled) audio.stopVoice();
    saveSettings();
    audio.playEffect('menu', settings.soundEffectsEnabled);
  });
  root.querySelector('[data-anger-meter]')?.addEventListener('animationend', event => { if (event.animationName === 'dash-anger-flash') event.currentTarget.classList.remove('is-flashing'); });
  partnerStage.addEventListener('animationend', event => { if (event.animationName === 'dash-partner-react') partnerStage.classList.remove('is-reacting'); });
  root.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      if (!menu.hidden) closeMenu();
      else if (!pausePanel.hidden) resume();
    }
    if (event.key === 'Tab' && !menu.hidden) {
      const focusable = [...menu.querySelectorAll('button:not(:disabled), input:not(:disabled)')];
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  });
  const handleVisibility = () => {
    if (!document.hidden) return;
    if (!menu.hidden) menuWasRunning = false;
    if (engine?.getState().status === 'playing') pause(false);
    audio.suspend();
    if (activeScreen === 'play' && menu.hidden && engine?.getState().status === 'paused') pausePanel.hidden = false;
  };
  document.addEventListener('visibilitychange', handleVisibility);

  applySettings();
  root.querySelector('[data-best-score]').textContent = String(storedBest);
  if (cloudSyncEnabled) void cloud.start();
  else cloud.start();

  window.addEventListener('pagehide', () => {
    if (gameOverTimeout) window.clearTimeout(gameOverTimeout);
    hideReaction();
    cloud.stop();
    document.removeEventListener('visibilitychange', handleVisibility);
    engine?.destroy();
    audio.destroy();
  }, { once: true });
  window.BBC_DIAPER_DASH = Object.freeze({
    start: begin,
    pause: () => pause(false),
    resume,
    openMenu,
    closeMenu,
    getState: () => engine?.getState() || null,
    getPreferences: () => personalisation.normalizePreferences(settings)
  });
})();

;
;/* bbc-dash-ga4-dom-hooks-v1 — Analytics-only observer, no gameplay/UI changes. */
(() => {
  'use strict';

  function initialize() {
  const root = document.querySelector('[data-bbc-diaper-dash]');
  const game = window.BBC_DIAPER_DASH;
  const tracker = window.BBC_GA4_EVENTS;
  if (!root || !game || root.dataset.bbcDashGa4Hooks === 'bbc-dash-ga4-dom-hooks-v1') return;
  if (!tracker) {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
    return;
  }
  root.dataset.bbcDashGa4Hooks = 'bbc-dash-ga4-dom-hooks-v1';

  const safeTrack = (name, fields = {}) => {
    try { tracker.trackGame(name, fields); } catch (_) { /* Analytics must never interrupt play. */ }
  };
  const run = {
    count: 0, active: false, ended: false, startedAt: 0, bestAtStart: 0,
    previous: null, timer: 0, pendingPauseSource: 'button',
    stats: { catch_pee: 0, catch_poop: 0, miss_pee: 0, miss_poop: 0 },
    summary: null,
  };
  const current = () => {
    try { return game.getState?.() || null; } catch (_) { return null; }
  };
  const elapsed = () => Math.max(0, Math.floor((performance.now() - run.startedAt) / 1000));
  const tier = state => Math.floor(Math.max(0, Number(state?.score) || 0) / 20);
  const common = state => ({ elapsed_seconds: elapsed(), difficulty_tier: tier(state) });

  function beginRun() {
    const replay = run.count > 0 && run.ended;
    if (replay && run.summary) safeTrack('game_replay', run.summary);
    run.count += 1;
    run.active = true;
    run.ended = false;
    run.startedAt = performance.now();
    run.stats = { catch_pee: 0, catch_poop: 0, miss_pee: 0, miss_poop: 0 };
    run.previous = null;
    run.summary = null;
    const before = current();
    run.bestAtStart = Math.max(0, Number(before?.best) || 0);
    safeTrack('game_start', { run_type: replay ? 'replay' : 'first', entry_point: 'diaper_dash_page' });
    if (!run.timer) run.timer = window.setInterval(sample, 250);
    window.setTimeout(() => { run.previous = current(); sample(); }, 0);
  }

  function sample() {
    if (!run.active) return;
    const state = current();
    if (!state) return;
    const before = run.previous;
    if (before) {
      const scoreGain = Math.max(0, Number(state.score) - Number(before.score));
      if (scoreGain) {
        const itemType = scoreGain === 5 ? 'pee' : scoreGain === 10 ? 'poop' : null;
        if (itemType) {
          run.stats[`catch_${itemType}`] += 1;
          safeTrack('game_catch', { item_type: itemType, score_awarded: scoreGain, ...common(state) });
        }
        const fromTier = tier(before);
        const toTier = tier(state);
        if (toTier > fromTier) safeTrack('game_difficulty_increase', {
          from_tier: fromTier, to_tier: toTier,
          speed_multiplier: Math.pow(1.1, toTier), baby_speed_multiplier: Math.pow(1.05, toTier),
        });
      }
      const angerGain = Math.max(0, Number(state.anger) - Number(before.anger));
      if (angerGain) {
        const itemType = angerGain === 5 ? 'pee' : angerGain === 10 ? 'poop' : null;
        if (itemType) {
          run.stats[`miss_${itemType}`] += 1;
          const angerBand = state.anger >= 30 ? 'critical' : state.anger >= 20 ? 'high' : state.anger >= 10 ? 'medium' : 'low';
          safeTrack('game_miss', { item_type: itemType, anger_added: angerGain, anger_band: angerBand, ...common(state) });
        }
      }
      if (before.status === 'playing' && state.status === 'paused') {
        safeTrack('game_pause', { pause_source: run.pendingPauseSource, ...common(state) });
        run.pendingPauseSource = 'button';
      } else if (before.status === 'paused' && state.status === 'playing') {
        safeTrack('game_resume', common(state));
      }
    }
    run.previous = state;
    if (state.status === 'game-over' && !run.ended) {
      run.ended = true;
      run.summary = {
        previous_score: Math.max(0, Number(state.score) || 0),
        previous_best: Math.max(0, Number(state.best) || 0),
        duration_seconds: elapsed(), difficulty_tier: tier(state),
      };
      safeTrack('game_over', {
        final_score: run.summary.previous_score, best_score: run.summary.previous_best,
        duration_seconds: run.summary.duration_seconds, difficulty_tier: run.summary.difficulty_tier,
        anger_final: Math.max(0, Number(state.anger) || 0),
        new_personal_best: Number(state.score) > run.bestAtStart,
        ...run.stats,
      });
      run.active = false;
      if (run.timer) window.clearInterval(run.timer);
      run.timer = 0;
    }
  }

  root.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target.closest('[data-action], [data-partner-choice]') : null;
    if (!target) return;
    const action = target.dataset.action;
    if (action === 'start' || action === 'play-again') beginRun();
    if (action === 'pause') run.pendingPauseSource = 'button';
    if (action === 'open-menu') {
      const state = current();
      safeTrack('game_menu_open', { game_state: state?.status || 'opening' });
      if (state?.status === 'playing') run.pendingPauseSource = 'menu';
    }
    if (action === 'close-menu' || action === 'resume') run.pendingPauseSource = 'menu';
    if (target.hasAttribute('data-partner-choice')) safeTrack('game_partner_select', { partner_id: target.dataset.partnerChoice });
  }, true);

  root.addEventListener('change', event => {
    const input = event.target instanceof Element ? event.target.closest('[data-setting]') : null;
    if (!input) return;
    const setting = input.dataset.setting === 'voice' ? 'voice_reactions' : input.dataset.setting;
    safeTrack('game_setting_change', { setting, enabled: Boolean(input.checked) });
  }, true);

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) run.pendingPauseSource = 'visibility';
    window.setTimeout(sample, 0);
  });
  window.addEventListener('pagehide', () => {
    const state = current();
    if (run.active && state && ['playing', 'paused'].includes(state.status)) {
      safeTrack('game_exit', {
        exit_state: state.status, final_score: Math.max(0, Number(state.score) || 0),
        duration_seconds: elapsed(), difficulty_tier: tier(state),
      });
    }
    if (run.timer) window.clearInterval(run.timer);
  }, { once: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
